import { useNavigate, useLocation } from "react-router-dom";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useTranslation } from "react-i18next";
import { ReaderIcon, ExclamationTriangleIcon, CheckCircledIcon, CrossCircledIcon, MinusCircledIcon, FileIcon, MixIcon, ChatBubbleIcon } from "@radix-ui/react-icons";
import { LuListTodo } from "react-icons/lu";
import { StepperState } from "@/constants/enums";
import { Dispatch, SetStateAction, useState, useEffect, JSX } from "react";
import ProgressHoverCard from "./ProgressHoverCard";
import { BidDecisionGroup, BidDecisionGroupPermissionEnum, DecisionEnum, MarginDecisionEnum, MarginDecisionGroup, MarginDecisionGroupPermissionEnum, TenderState } from "@/graphql/__generated__/graphql";
import useUserStore from "@/store/shield";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ExperimentalFeatureModal } from "@/components/shared/header";
import Cookies from "js-cookie";
import { ChartBarIcon, SettingsIcon, CalculatorIcon, SheetIcon } from "lucide-react";
import { cn } from "@/lib/utils";

type Sublink = {
  label?: string;
  icon?: JSX.Element;
  route: string;
  baseroute?: string;
  disabled?: boolean;
  isNew?: boolean;
  onClick?: () => void;
};

/**
 * Function to check if a bidding organization sublink is active based on the current path.
 *
 * This is a custom check specifically for the bidding organization sublinks which uses
 * url parameters to determine if the sublink is active instead of the route.
 * @param sublinkRoute - The route of the sublink to check if it is active
 * @returns - A boolean indicating if the sublink is active
 */
function isBiddingOrganizationSubLinkActive(sublinkRoute: string) {
  const currentUrl = new URL(window.location.href);
  const currentPath = currentUrl.pathname;
  const currentId = currentUrl.searchParams.get("operationalOrganizationId");

  // Extract the base route from the sublink (e.g., "services/all")
  const [baseRoute] = sublinkRoute.split("?");

  // If the current path doesn't include the baseRoute, it's not active.
  if (!currentPath.includes(baseRoute)) {
    return false;
  }

  // handle the query parameters
  const sublinkParams = new URLSearchParams(sublinkRoute.split("?")[1] || "");
  const sublinkId = sublinkParams.get("operationalOrganizationId");

  // If this is the "All" link (no sublinkId), it's active only if there's no opOrgId in the URL
  if (!sublinkId) {
    return currentId === null;
  }

  // active if the opOrgId matches
  return currentId === sublinkId;
}

/**
 * Function to check if a navlink is active based on the current path.
 * @param navlinkObj - The navlink object to check if it is active
 * @returns - A boolean indicating if the navlink is active
 */
function isNavlinkActive(navlinkObj: Sublink) {
  const currentPath = location.pathname;
  const navlinkRoute = navlinkObj.baseroute ?? navlinkObj.route;

  if (!navlinkRoute) return false;

  // Escape special characters in navlinkRoute for regex
  const escapedRoute = navlinkRoute.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");

  // Create a regex to match the navlinkRoute as a path segment
  const regex = new RegExp(`(^|/)${escapedRoute}(/|$)`);

  return regex.test(currentPath);
}

const renderStepperItem = (navlinkObj: any, index: number, isLastItem: boolean, handleNavigate: any, expandedAccordion: string | null, setExpandedAccordion: Dispatch<SetStateAction<string | null>>) => {
  const stateClassMap = {
    [StepperState.CLOSED]: {
      bg: "bg-red-400",
      border: "border-red-400",
    },
    [StepperState.DONE]: {
      bg: "bg-green-400",
      border: "border-green-400",
    },
    [StepperState.CURRENT]: {
      bg: "bg-yellow-300",
      border: "border-yellow-300",
    },
    [StepperState.PENDING]: {
      bg: "bg-gray-300",
      border: "border-gray-300",
    },
  };

  const stateClasses = stateClassMap[navlinkObj.state];

  const stateBgColour: string = stateClasses.bg;
  const stateBorderColour: string = stateClasses.border;

  const isActive = isNavlinkActive(navlinkObj);

  // Check if any sublink is active
  const isAnySublinkActive =
    navlinkObj.type === "accordion" &&
    navlinkObj.sublinks.some((sublink: Sublink) => isNavlinkActive(sublink));

  // Group sublinks into enabled and disabled
  const enabledSublinks = navlinkObj.type === "accordion" ? navlinkObj.sublinks.filter((sublink: Sublink) => !sublink.disabled) : [];
  const disabledSublinks = navlinkObj.type === "accordion" ? navlinkObj.sublinks.filter((sublink: Sublink) => sublink.disabled) : [];

  return (
    // CONTAINER
    <div className="flex flex-row flex-start" key={`side-navlink-container-${index}`}>
      {/* STEPPER */}
      <div className={cn("relative top-0 pl-2 pr-1", navlinkObj.type === "accordion" ? "pt-[10px]" : "pt-[6px]")}>
        <div className={cn("h-4 w-4", stateBgColour, "rounded-full")}></div>
        {
          !isLastItem && (
            <div className={cn("h-full border-l-2", stateBorderColour, "absolute top-5 left-[15px]")}></div>
          )
        }
      </div>

      {/* ITEM */}
      {
        navlinkObj.type === "accordion" ? (
          <Accordion
            type="single"
            collapsible
            className="w-full"
            value={expandedAccordion === `item-${index}` ? `item-${index}` : undefined}
            onValueChange={(value) => setExpandedAccordion(value)}
          >
            <AccordionItem value={`item-${index}`}>
              <AccordionTrigger className="text-sm font-normal text-slate-400 py-2">
                <span className={cn(
                  "text-left pl-2",
                  navlinkObj.state === StepperState.PENDING ? "text-slate-400" : "text-slate-800",
                  isAnySublinkActive && "font-bold"
                )}>
                  {navlinkObj.label}
                </span>
              </AccordionTrigger>
              <AccordionContent className="flex flex-col gap-1 pb-2">
                {/* Render Enabled Sublinks */}
                {
                  enabledSublinks.map((sublink: Sublink) => {
                    let isSublinkActive;

                    // If is bidding organization routes:
                    if (sublink.route.startsWith("services/all")) {
                      isSublinkActive = isBiddingOrganizationSubLinkActive(sublink.route);
                    } else {
                      isSublinkActive = isNavlinkActive(sublink);
                    }

                    return (
                      <button
                        key={`enabled-sublink-${sublink.label}`}
                        className={cn(
                          "flex items-center justify-between py-1 px-2 rounded-md text-sm text-left",
                          isSublinkActive && "tender-side-nav-btn-active"
                        )}
                        onClick={() => handleNavigate(sublink.route)}
                        disabled={sublink.disabled}
                      >
                        <span>{sublink.label}</span>
                        {sublink.icon}
                      </button>
                    );
                  })
                }

                {/* Render Separator if there are both enabled and disabled sublinks */}
                {enabledSublinks.length > 0 && disabledSublinks.length > 0 && (
                  <Separator className="mt-2" />
                )}

                {/* Render Disabled Sublinks */}
                {
                  disabledSublinks.map((sublink: Sublink) => {

                    return (
                      <button
                        key={`disabled-sublink-${sublink.label}`}
                        className="flex items-center justify-between py-1 px-2 rounded-md text-sm text-left text-slate-400 cursor-not-allowed"
                        disabled
                      >
                        <span>
                          {sublink.label}
                        </span>

                        {sublink.icon}
                      </button>
                    );
                  })
                }
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        ) : (
          <button
            className={cn(
              "whitespace-nowrap grow py-1 px-2 rounded-md text-sm text-left",
              navlinkObj.state === StepperState.PENDING ? "text-slate-400" : "hover:bg-gray-200",
              isActive && "tender-side-nav-btn-active hover:bg-slate-900/90"
            )}
            onClick={() => handleNavigate(navlinkObj.route)}
            disabled={navlinkObj.state === StepperState.PENDING}
          >
            <span>{navlinkObj.label}</span>
          </button>
        )
      }
    </div>
  );
};

const getIcon = (group: Partial<BidDecisionGroup>, organizationId: number) => {
  if (group.awaitingOrganizationId === organizationId) {
    return <ExclamationTriangleIcon className="icon-xs shrink-0 text-orange-400" />;
  } else if (group.awaitingOrganizationId === null) {
    if (group.currentDecision === DecisionEnum.Bid) {
      return <CheckCircledIcon className="icon-xs shrink-0 text-green-400" />;
    } else if (group.currentDecision === DecisionEnum.Nobid) {
      return <CrossCircledIcon className="icon-xs shrink-0 text-red-400" />;
    } else {
      return null;
    }
  } else {
    return <MinusCircledIcon className="icon-xs shrink-0 text-yellow-400" />;
  }
};

/**
 * Returns the appropriate icon for a margin decision group based on its state.
 * @param group - The margin decision group
 * @param organizationId - The current organization ID
 * @returns - The icon element or null
 */
const getMarginDecisionIcon = (group: Partial<MarginDecisionGroup>, organizationId: number) => {
  if (group.awaitingOrganizationId === organizationId) {
    return <ExclamationTriangleIcon className="icon-xs shrink-0 text-orange-400" />;
  } else if (group.awaitingOrganizationId === null) {
    if (group.currentDecision === MarginDecisionEnum.Approve || group.currentDecision === MarginDecisionEnum.OverruleApprove) {
      return <CheckCircledIcon className="icon-xs shrink-0 text-green-400" />;
    } else if (group.currentDecision === MarginDecisionEnum.Reject || group.currentDecision === MarginDecisionEnum.OverruleReject) {
      return <CrossCircledIcon className="icon-xs shrink-0 text-red-400" />;
    } else {
      return null;
    }
  } else {
    return <MinusCircledIcon className="icon-xs shrink-0 text-yellow-400" />;
  }
};

interface Props {
  tenderName: string;
  inSheet?: boolean;
  setIsOpen?: Dispatch<SetStateAction<boolean>>;
  bidDecisionGroups: Partial<BidDecisionGroup>[];
  marginDecisionGroups: Partial<MarginDecisionGroup>[];
  tenderState: TenderState | null;
  isDocumentChat?: boolean;
  tenderID?: string;
  chatMessages?: number;
};

const COOKIE_NAME = "hide_experimental_feature_modal_";
const featureID = "document_chat";

function TenderSideNavigationMenu({
  tenderName,
  inSheet,
  setIsOpen,
  bidDecisionGroups,
  marginDecisionGroups,
  tenderState,
  isDocumentChat,
  tenderID,
  chatMessages = 0,
}: Props) {

  // Hooks
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const organizationId = useUserStore((state) => state.orgId);

  // States
  const [showExperimentalFeaturetModal, setShowExperimentalFeatureModal] = useState<boolean>(false);
  const [expandedAccordion, setExpandedAccordion] = useState<string | null>(null);

  // Effect to handle automatic accordion expansion based on URL
  useEffect(() => {
    const path = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const openAccordion = searchParams.get("openAccordion");

    if (openAccordion) {
      setExpandedAccordion(openAccordion);
      // Remove the query parameter to avoid reopening on page refresh
      searchParams.delete("openAccordion");
      const newSearch = searchParams.toString();
      const newPath = newSearch ? `${path}?${newSearch}` : path;
      navigate(newPath, { replace: true });
      return;
    }

    // Check if we're on a bid decision page
    if (path.includes("/biddecisions/")) {
      setExpandedAccordion("item-0"); // Index of bid decisions in progressNavlinks
    }
    // Check if we're on a margin decision page
    else if (path.includes("/margindecisions/")) {
      setExpandedAccordion("item-2"); // Index of margin decisions in progressNavlinks
    }
    // Check if we're on a services page
    else if (path.includes("/services/")) {
      setExpandedAccordion("item-1"); // Index of calculations in progressNavlinks
    }
  }, [location.pathname]);

  /**
   * Checks if a link should be disabled based on whether the specified permission exists in the group's permissions array.
   * @param group - The decision group (either BidDecisionGroup or MarginDecisionGroup)
   * @param viewPermission - The View permission enum value to check for
   * @returns - True if the link should be disabled (View permission not present), false otherwise
   */
  const shouldDisableLink = <T extends BidDecisionGroupPermissionEnum | MarginDecisionGroupPermissionEnum>(
    group: Partial<BidDecisionGroup> | Partial<MarginDecisionGroup>,
    viewPermission: T
  ): boolean => {
    const permissions = (group.permissions ?? []) as Array<BidDecisionGroupPermissionEnum | MarginDecisionGroupPermissionEnum>;
    return !permissions.includes(viewPermission);
  };

  const bidDecisionsSubLinks = bidDecisionGroups.map((group) => {
    return {
      label: group.operationalOrganizationShortName,
      route: `biddecisions/${group.id}`,
      disabled: shouldDisableLink(group, BidDecisionGroupPermissionEnum.View),
      icon: getIcon(group, organizationId),
    };
  });

  const marginDecisionSubLinks = marginDecisionGroups.map((group) => {
    return {
      label: group.operationalOrganizationShortName,
      route: `margindecisions/${group.id}`,
      disabled: shouldDisableLink(group, MarginDecisionGroupPermissionEnum.View),
      icon: getMarginDecisionIcon(group, organizationId),
    };
  });

  const biddingOrganizationsSubLinks = [
    {
      label: t("edittender.navlinks.progress.calculations.allservices"),
      route: "services/all",
    },
    ...bidDecisionGroups
      .filter((group) => group.currentDecision === DecisionEnum.Bid)
      .map((group) => ({
        label: group.operationalOrganizationShortName,
        route: `services/all?operationalOrganizationId=${group.operationalOrganizationId}`,
        isCurrentOrganization: group.operationalOrganizationId === organizationId, // later use this to enable and disable the links
      })),
  ];

  const informationNavLinks = [
    {
      label: t("edittender.navlinks.information.tenderdetails"),
      icon: <ReaderIcon className="h-4 w-4 shrink-0" />,
      route: "details",
    },
    {
      label: t("edittender.navlinks.information.executivesummary"),
      icon: <MixIcon className="h-4 w-4 shrink-0" />,
      route: "executivesummary",
    },
    {
      label: t("edittender.navlinks.information.documents"),
      icon: <FileIcon className="h-4 w-4 shrink-0" />,
      route: "documents",
    },
    {
      label: t("edittender.navlinks.information.analytics"),
      icon: <ChartBarIcon className="h-4 w-4 shrink-0" />,
      route: "analytics",
      disabled: tenderState === TenderState.PendingBidDecision
    },
    {
      label: t("edittender.navlinks.information.calculationoverview"),
      icon: <CalculatorIcon className="h-4 w-4 shrink-0" />,
      route: "calculation-overview",
      disabled: tenderState === TenderState.PendingBidDecision
    }
  ];

  const progressNavlinks = [
    {
      label: t("edittender.navlinks.progress.biddecisions"),
      route: "",
      type: "accordion",
      sublinks: bidDecisionsSubLinks,
    },
    {
      label: t("edittender.navlinks.progress.calculations.title"),
      route: "",
      type: "accordion",
      sublinks: biddingOrganizationsSubLinks,
    },
    {
      label: t("edittender.navlinks.progress.margindecisions"),
      route: "margindecisions",
      type: "accordion",
      sublinks: marginDecisionSubLinks
    },
    {
      label: t("edittender.navlinks.progress.submission"),
      route: "submission",
    }
  ];

  const manageNavLinks = [
    {
      label: t("edittender.navlinks.manage.sheetagent"),
      icon: <SheetIcon className="h-4 w-4 shrink-0" />,
      route: "sheet-agent",
      isNew: false,
    },
    {
      label: t("edittender.navlinks.manage.chat"),
      icon: <ChatBubbleIcon className="h-4 w-4 shrink-0" />,
      route: "chat",
      hasIndicator: true,
    },
    {
      label: t("edittender.navlinks.manage.tasks"),
      icon: <LuListTodo className="h-4 w-4 shrink-0" />,
      route: "tasksboard",
    },
    {
      label: t("edittender.navlinks.manage.options"),
      icon: <SettingsIcon className="h-4 w-4 shrink-0" />,
      route: "options",
    }
  ];

  /**
   * Navigates to a specified route while preserving existing query parameters.
   * @param {string} route - The base route to navigate to. This should include the path but not the query parameters.
   * @returns {void}
  */
  const handleNavigate = (route: string): void => {
    navigate(route);

    if (inSheet && setIsOpen) {
      setIsOpen(false);
    }
  };

  /**
   * Function to get the current step index based on the tender state.
   * The index is relative to the progressNavlinks array.
   * @param tenderState - The current tender state
   * @returns - The index of the current step
   */
  function getCurrentStepIndex(tenderState: TenderState | null) {
    switch (tenderState) {
    case TenderState.NotBidding:
    case TenderState.PendingBidDecision:
      return 0;
    case TenderState.Editing:
      return 1;
    case TenderState.PendingMarginDecision:
      return 2;
    case TenderState.ReadyToSubmit:
      return 3;
    case TenderState.Submitted:
    case TenderState.Won:
      return progressNavlinks.length; // All steps done
    case TenderState.Lost:
      return progressNavlinks.length; // All steps done
    default:
      return 0;
    }
  }

  /**
   * Function to update the progress navlinks based on the current tender state.
   * @param tenderState - The current tender state
   * @returns - The updated progress navlinks
   */
  function updateProgressNavlinks(tenderState: TenderState | null) {
    const currentStepIndex = getCurrentStepIndex(tenderState);

    const updatedNavlinks = progressNavlinks.map((link, index) => {
      let state: StepperState;

      // Set all state before current to DONE
      if (index < currentStepIndex) {
        state = StepperState.DONE;
      } else if (index === currentStepIndex) {
        if (tenderState === TenderState.NotBidding || tenderState === TenderState.Annulment) {
          // Set the current step to CLOSED when tenderState is NotBidding
          state = StepperState.CLOSED;
        } else {
          state = StepperState.CURRENT;
        }
      } else {
        // Set all state after current to PENDING
        state = StepperState.PENDING;
      }

      return { ...link, state };
    });

    return updatedNavlinks;
  }


  const experimentalFeatureActionFn = () => {
    navigate(`/tender/${tenderID}/documentchat`);
    setShowExperimentalFeatureModal(false);
  };

  const handleOpenExperimentalFeatureModal = () => {
    const hideModal = Cookies.get(COOKIE_NAME + featureID);
    if (hideModal === "true") {
      navigate(`/tender/${tenderID}/documentchat`);
      return;
    }
    setShowExperimentalFeatureModal(true);
  };

  const progressNavItems = updateProgressNavlinks(tenderState);

  return (
    <div className={cn("h-full min-w-3 md:pb-[100px]", inSheet ? "w-full" : "min-w-[220px] max-w-[240px]")}>
      <ScrollArea className="h-full px-2 sm:px-4 overflow-y-auto">
        <div className="sticky top-0 flex flex-col gap-2 pt-2" data-cy="side-nav-container">
          <div className="text-sm font-semibold max-w-[180px] truncate">
            {tenderName}
          </div>

          <Button
            size="xs"
            onClick={handleOpenExperimentalFeatureModal}
            className={cn("hover:bg-blue-300 py-1", !isDocumentChat && "bg-blue-400")}
          >
            <SparklesIcon className="w-[18px] h-[18px] text-white shrink-0 mr-1" />
            {t("components:documentchat.buttons.chat")}
          </Button>

          {/* Information nav card */}
          <div className="p-2 bg-slate-100 rounded-md">
            <h1 className="text-sm text-slate-400 py-1 px-2">{t("edittender.navlinks.information.title")}</h1>
            <div className="flex flex-col gap-2">
              {
                informationNavLinks.map((link) => {
                  const isActive = isNavlinkActive(link);

                  return (
                    <button
                      key={`information-link-${link.label}`}
                      className={cn("tender-side-nav-btn", isActive && "tender-side-nav-btn-active", link.disabled && "text-slate-400 hover:bg-slate-100")}
                      onClick={() => handleNavigate(link.route)}
                      disabled={link.disabled}
                    >
                      {link.icon}
                      <span className="pl-1">{link.label}</span>
                    </button>
                  );
                })
              }
            </div>
          </div>

          {/* Progress nav card */}
          <div className="p-2 bg-slate-100 rounded-md">
            <div className="flex items-center justify-between">
              <h1 className="text-sm text-slate-400 py-1 px-2">{t("edittender.navlinks.progress.title")}</h1>
              <ProgressHoverCard />
            </div>
            <div className="flex flex-col gap-2">
              {
                progressNavItems.map((navlinkObj, index) => {
                  const isLastItem = index === progressNavlinks.length - 1;

                  return renderStepperItem(navlinkObj, index, isLastItem, handleNavigate, expandedAccordion, setExpandedAccordion);
                })
              }
            </div>
          </div>

          {/* Manage nav card */}
          <div className="p-2 bg-slate-100 rounded-md">
            <h1 className="text-sm text-slate-400 py-1 px-2">{t("edittender.navlinks.manage.title")}</h1>
            <div className="flex flex-col gap-2">
              {
                manageNavLinks.map((link) => {
                  const isActive = isNavlinkActive(link);

                  return (
                    <button
                      key={`other-link-${link.label}`}
                      className={cn("relative tender-side-nav-btn flex", isActive && "tender-side-nav-btn-active")}
                      onClick={() => handleNavigate(link.route)}
                    >
                      {link.icon}
                      <span className="pl-1">{link.label}</span>
                      {link.hasIndicator && chatMessages > 0 && !isActive && (
                        <div className="absolute top-1 right-1 size-5 flex items-center justify-center text-white bg-black rounded-full text-xs">
                          {chatMessages}
                        </div>
                      )}
                      {link.isNew && (
                        <div className="text-xs bg-blue-500 text-white px-2 py-0.5 rounded-md ml-auto">
                          {t("common:new")}
                        </div>
                      )}
                    </button>
                  );
                })
              }
            </div>
          </div>
        </div>
      </ScrollArea>

      <ExperimentalFeatureModal
        featureID={featureID}
        open={showExperimentalFeaturetModal}
        onOpenChange={setShowExperimentalFeatureModal}
        actionFn={experimentalFeatureActionFn}
      />
    </div>
  );
}

export default TenderSideNavigationMenu;
